import React from 'react';
import type { Step, TooltipRenderProps } from 'react-joyride';
import { XIcon } from '../SvgIconsV3/X';

import {
  Container,
  Content,
  Actions,
  Progress,
  SkipLink,
  Footer,
  PrimaryButton,
} from './TourTooltip.styles';
import { LinkV2 } from '../LinkV2';

export type TourTooltipProps = TooltipRenderProps & {
  step: Step & {
    nextButtonText?: string; // custom prop that we allow to be passed with Step
    /**
     * @deprecated The end tour text is no longer customizable
     */
    endTourText?: string; // custom prop that we allow to be passed with Step
  };
};

/**
 * Renders a custom tooltip for the Joyride package, which is passed to to the Joyride component as the `tooltipComponent`
 *
 * The content of the tooltip is customizable, but to inherit the default styles, use an `<h3>` tag for your title, and a `<p>` tag for your content body.
 *
 * ```
 * import { TourTooltip } from '@zola/zola-ui/src/components/TourTooltip';
 *
 * <Joyride tooltipComponent={TourTooltip} />
 * ```
 */
const TourTooltip = ({
  size,
  index,
  continuous,
  isLastStep,
  step,
  tooltipProps,
  skipProps,
  backProps,
  primaryProps,
}: TourTooltipProps): JSX.Element => {
  const { nextButtonText, content, showSkipButton, showProgress, hideBackButton } = step;

  const getButtonText = () => {
    if (nextButtonText) {
      return nextButtonText;
    }
    if (!continuous) {
      return 'Close';
    }
    if (!isLastStep) {
      return 'Next';
    }
    if (size === 1) {
      return 'Got It';
    }
    return 'Done';
  };

  return (
    <Container {...tooltipProps}>
      {content && <Content>{content}</Content>}
      <Footer>
        {showProgress && size > 1 && <Progress>Step {`${index + 1} of ${size}`}</Progress>}
        <Actions>
          {index > 0 && !hideBackButton && (
            <LinkV2
              {...backProps}
              role="button"
              onClick={backProps.onClick as (e: React.MouseEvent) => void}
            >
              Previous
            </LinkV2>
          )}
          <PrimaryButton {...primaryProps} role="button" onClick={primaryProps.onClick}>
            {getButtonText()}
          </PrimaryButton>
        </Actions>
      </Footer>
      {showSkipButton !== false && (
        <SkipLink
          {...skipProps}
          role="button"
          onClick={skipProps.onClick as (e: React.MouseEvent) => void}
        >
          <XIcon width={22} height={22} />
        </SkipLink>
      )}
    </Container>
  );
};

export default TourTooltip;
